import { ModelViewerModule } from '@akso/model-viewer';
import { provideHttpClient, withInterceptors, withXhr } from '@angular/common/http';
import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  provideAppInitializer,
} from '@angular/core';
import { provideDateFnsAdapter } from '@angular/material-date-fns-adapter';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatIconRegistry } from '@angular/material/icon';
import { MAT_SELECT_CONFIG } from '@angular/material/select';
import { MAT_TABS_CONFIG } from '@angular/material/tabs';
import { MAT_TOOLTIP_DEFAULT_OPTIONS } from '@angular/material/tooltip';
import { DomSanitizer } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  PreloadAllModules,
  provideRouter,
  withComponentInputBinding,
  withPreloading,
  withRouterConfig,
} from '@angular/router';
import { ApplicationinsightsAngularpluginErrorService } from '@microsoft/applicationinsights-angularplugin-js';
import { provideGlobalGridOptions } from 'ag-grid-community';
import { enGB } from 'date-fns/locale';
import { provideToastr } from 'ngx-toastr';
import { routes } from 'src/app/app.routes';
import { cachingInterceptor } from 'src/app/core/interceptors/http-cache.interceptor';
import { httpErrorInterceptor } from 'src/app/core/interceptors/http-error.interceptor';
import { jwtHttpInterceptor } from 'src/app/core/interceptors/http-token.interceptor';
import { AuthLocalService } from 'src/app/core/services/auth-local.service';
import { AuthService } from 'src/app/core/services/auth.service';
import { RequestCache, RequestCacheWithMap } from 'src/app/core/services/cache/request-cache.service';
import { UserConfigService } from 'src/app/core/services/user-config.service';
import { ViewAccessService } from 'src/app/core/services/view-access.service';
import { CustomDSIconModule } from 'src/app/ds-icon.module';
import { ENV } from 'src/app/shared/env/environment.token';
import { environment } from 'src/environments/environment';
import { iconLoader } from './app/icons/icon-loader';
import { CUSTOM_DATE_TIME_FORMAT } from './constants';

provideGlobalGridOptions({
  accentedSort: true,
  tooltipShowDelay: 0,
  theme: 'legacy',
});

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(
      routes,
      withComponentInputBinding(),
      withPreloading(PreloadAllModules),
      withRouterConfig({ onSameUrlNavigation: 'reload' }),
      // withDebugTracing(),
    ),
    provideAnimations(),
    provideHttpClient(
      withXhr(),
      withInterceptors([jwtHttpInterceptor, cachingInterceptor, httpErrorInterceptor]),
    ),
    provideToastr({
      maxOpened: 5,
      preventDuplicates: true,
      timeOut: 6000,
    }),
    importProvidersFrom(CustomDSIconModule, ModelViewerModule),
    ViewAccessService,
    {
      provide: ErrorHandler,
      useClass: ApplicationinsightsAngularpluginErrorService,
    },
    {
      provide: AuthService,
      useClass: environment.authentication ? AuthService : AuthLocalService,
    },
    { provide: RequestCache, useClass: RequestCacheWithMap },
    { provide: ENV, useValue: environment },
    provideAppInitializer(() => {
      const initializerFn = (
        (config: UserConfigService) => () =>
          config.initUserConfig()
      )(inject(UserConfigService));
      return initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn = iconLoader(inject(DomSanitizer), inject(MatIconRegistry));
      return initializerFn();
    }),
    {
      provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
      useValue: { showDelay: 1000, hideDelay: 800, touchendHideDelay: 1000 },
    },
    { provide: MAT_TABS_CONFIG, useValue: { animationDuration: '0', stretchTabs: false } },
    { provide: MAT_DIALOG_DEFAULT_OPTIONS, useValue: { autoFocus: false } },
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'outline', floatLabel: 'always' } },
    { provide: MAT_SELECT_CONFIG, useValue: { hideSingleSelectionIndicator: true } },
    { provide: MAT_DATE_LOCALE, useValue: enGB },
    provideDateFnsAdapter(CUSTOM_DATE_TIME_FORMAT),
  ],
};
